"use client";

import { useState, useEffect, useRef } from "react";
import { Check, Copy, X } from "lucide-react";

type Props = {
  title: string;
  description?: string;
};

export default function SharePopup({ title }: Props) {
  const [href, setHref] = useState("");
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setHref(window.location.href);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open]);

  // Open our own share popup directly — no native OS share sheet.
  const handleShare = () => setOpen(true);

  const shareLinks = [
    {
      label: "Facebook",
      color: "#1877F2",
      bg: "#eff6ff",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2" aria-hidden>
          <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
        </svg>
      ),
      onClick: () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(href)}`, "_blank", "width=600,height=440,noopener"),
    },
    {
      label: "WhatsApp",
      color: "#25d366",
      bg: "#f0fdf4",
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="#25d366" aria-hidden>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
      onClick: () => window.open(`https://wa.me/?text=${encodeURIComponent(`${title} ${href}`)}`, "_blank", "noopener"),
    },
    {
      label: "X / Twitter",
      color: "#000",
      bg: "#f8fafc",
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#000" aria-hidden>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      onClick: () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(href)}`, "_blank", "width=600,height=440,noopener"),
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* blocked */ }
  };

  return (
    <>
      {/* Trigger */}
      <button
        type="button"
        onClick={handleShare}
        className="inline-flex shrink-0 items-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold text-white transition hover:opacity-90 active:scale-[0.98]"
        style={{ background: "#1877F2" }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="white" aria-hidden>
          <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.41c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.97h-1.513c-1.491 0-1.956.93-1.956 1.886v2.267h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
        </svg>
        Share on Facebook
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div
            ref={modalRef}
            className="w-full max-w-sm overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl"
            style={{ boxShadow: "0 24px 64px rgba(0,0,0,0.18)" }}
          >
            {/* Handle */}
            <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-[#dde1e7] sm:hidden" />

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <div>
                <p className="text-base font-bold text-[#0f172a]">Share this page</p>
                <p className="mt-0.5 text-sm text-[#6b7280] line-clamp-1">{title}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full transition hover:bg-[#f1f5f9]"
                aria-label="Close"
              >
                <X className="h-4 w-4 text-[#9ca3af]" />
              </button>
            </div>

            {/* Share options */}
            <div className="grid grid-cols-3 gap-3 px-5 pb-4">
              {shareLinks.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={s.onClick}
                  className="flex flex-col items-center gap-2 rounded-2xl py-4 text-xs font-bold transition hover:opacity-80 active:scale-[0.97]"
                  style={{ background: s.bg, color: s.color }}
                >
                  {s.icon}
                  {s.label}
                </button>
              ))}
            </div>

            {/* Copy link */}
            <div className="border-t border-[#f1f5f9] px-5 py-4">
              <div
                className="flex items-center gap-3 rounded-2xl px-4 py-3"
                style={{ background: "#f8fafc", border: "1.5px solid #dde1e7" }}
              >
                <p className="min-w-0 flex-1 truncate text-sm text-[#6b7280]">{href}</p>
                <button
                  type="button"
                  onClick={copyLink}
                  className="shrink-0 rounded-xl px-3 py-1.5 text-xs font-bold transition active:scale-[0.97]"
                  style={copied
                    ? { background: "#f0fdf4", color: "#16a34a" }
                    : { background: "#1877F2", color: "#fff" }
                  }
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
